'use client'
import React, { useState, useEffect } from 'react'
import { Search, Eye, Users, Globe, AlertTriangle, CheckCircle, XCircle, Calendar, Shield, Activity, Ban } from 'lucide-react'
import { socket } from '../Containers/MainContainer'

function PlutoIPDet({ second_page }) {
  const [userProfile, setUserProfile] = useState({})
  const [earnings, setEarnings] = useState([0, 0, 0, 0, 0])
  const [page, setPage] = useState(1)
  const [pageNumber, setPageNumber] = useState(-1)
  const [userId, setUserId] = useState(0)
  const [canUse, setCanUse] = useState(true)
  const [username, setUsername] = useState('')
  const [users, setUsers] = useState([])
  const [ip, setIP] = useState({})
  const [rank, setRank] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('asc')

  useEffect(() => {
    socket.on('response_get_admin_ipdetails', (data) => {
      setEarnings(data.withdrawals)
      setIP(data.ip)

      setPageNumber(Math.ceil(data.withdrawalsLength / 5))
      setUserProfile(data)
      setRank(data.rank)
    })

    socket.on('response_get_admin_ipdetails_users', (data) => {
      setUsers(data.users)
      setRank(data.rank)
      setLoading(false)
    })

    socket.on('response_get_admin_ipdetails_ip', (data) => {
      setIP(data.ip)
    })

    socket.emit('getIPDetailsAdmin', {
      ip: second_page,
    })

    socket.on('reTakeIpDet', () => {
      socket.emit('getIPDetailsAdmin', {
        ip: second_page,
      })
    })

    return () => {
      socket.off('response_get_admin_ipdetails')
      socket.off('response_get_admin_ipdetails_users')
      socket.off('response_get_admin_ipdetails_ip')
      socket.off('reTakeIpDet')
    }
  }, [second_page])

  // Filter and sort users
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id?.toString().includes(searchTerm) ||
    user.country?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortBy]
    let bVal = b[sortBy]
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleBanIP = () => {
    socket.emit('adminAction', {
      action: 'ban_ip',
      ip: second_page,
    })
  }

  const getIPStatusBadge = (isBad) => {
    if (isBad === true) {
      return (
        <div className="flex items-center gap-1 text-red-400">
          <XCircle className="h-4 w-4" />
          <span>Banned</span>
        </div>
      )
    } else if (isBad === false) {
      return (
        <div className="flex items-center gap-1 text-green-400">
          <CheckCircle className="h-4 w-4" />
          <span>Active</span>
        </div>
      )
    }
    return (
      <div className="flex items-center gap-1 text-gray-400">
        <Activity className="h-4 w-4" />
        <span>Unknown</span>
      </div>
    )
  }

  const StatCard = ({ icon: Icon, title, value, color = "blue" }) => (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon className={`h-5 w-5 text-${color}-400`} />
        </div>
        <div>
          <div className="text-sm font-medium text-gray-400">{title}</div>
          <div className="text-xl font-bold text-gray-200">{value}</div>
        </div>
      </div>
    </div>
  )

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-gray-800 rounded-lg p-4 animate-pulse">
          <div className="grid grid-cols-6 gap-4">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="h-8 w-8 text-blue-400" />
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-200">
              IP Address Details
            </h1>
          </div>
          <p className="text-gray-400">
            Analysis for IP: <span className="text-blue-400 font-mono">{second_page}</span>
          </p>
        </div>

        {/* IP Information Card */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 mb-6">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              IP Security Information
            </h2>
          </div>
          <div className="p-4">
            {loading && !ip.id ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-gray-700 rounded h-16 animate-pulse"></div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">ID</div>
                  <div className="text-sm font-mono text-gray-200">#{ip.id || 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Country</div>
                  <div className="text-sm font-semibold text-blue-400">{ip.country || 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">City</div>
                  <div className="text-sm text-gray-200">{ip.city || 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Provider</div>
                  <div className="text-sm text-gray-200">{ip.isp || 'N/A'}</div>
                </div>
                <div className="bg-gray-900 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">Rejection</div>
                  <div className="text-sm">{ip.rejection}</div>
                </div>
              </div>
            )}
            {/* <div className="flex justify-end">
              <button
                onClick={handleBanIP}
                disabled={ip.is_bad === undefined}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  ip.is_bad === true
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } disabled:bg-gray-600 disabled:cursor-not-allowed`}
              >
                <Ban className="h-4 w-4" />
                {ip.is_bad === undefined ? 'Loading...' : ip.is_bad === true ? 'Unban IP' : 'Ban IP'}
              </button>
            </div> */}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard 
            icon={Users} 
            title="Total Users" 
            value={users.length}
            color="blue" 
          />
          <StatCard 
            icon={Globe} 
            title="Countries" 
            value={[...new Set(users.map(u => u.country))].length}
            color="green" 
          />
          <StatCard 
            icon={Calendar} 
            title="Active Users" 
            value={users.length}
            color="purple" 
          />
        </div>

        {/* Users Table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          <div className="p-4 border-b border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-200">
                Users from IP: {second_page}
              </h2>
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, username, or country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full bg-gray-900 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 border border-gray-600"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-4">
                <LoadingSkeleton />
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    {[
                      { key: 'id', label: 'ID' },
                      { key: 'username', label: 'Username' },
                      { key: 'country', label: 'Country' },
                      { key: 'joinDate', label: 'Join Date' },
                      { key: 'actions', label: 'Actions' }
                    ].map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider ${
                          column.key !== 'actions' ? 'cursor-pointer hover:text-gray-300' : ''
                        }`}
                        onClick={() => column.key !== 'actions' && handleSort(column.key)}
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {sortBy === column.key && (
                            <span className="text-blue-400">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-700/20 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono text-gray-300">
                        #{user.id}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-200">{user.username}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                            {user.country}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(user.joinDate)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`/pluto/user/${user.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          <Eye className="h-3 w-3" />
                          Check User
                        </a>
                      </td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && !loading && (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No users found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Analysis Summary */}
        {!loading && users.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 mt-6">
            <h3 className="text-lg font-semibold text-gray-200 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              IP Analysis Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Countries detected:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {[...new Set(users.map(u => u.country))].length} 
                  {" "}({[...new Set(users.map(u => u.country))].join(', ')})
                </span>
              </div>
              <div>
                <span className="text-gray-400">Registration span:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {users.length > 0 && users.every(u => u.joinDate) &&
                    `${Math.ceil((new Date(Math.max(...users.map(u => new Date(u.joinDate)))) - 
                      new Date(Math.min(...users.map(u => new Date(u.joinDate))))) / (1000 * 60 * 60 * 24))} days`
                  }
                </span>
              </div>
              <div>
                <span className="text-gray-400">Risk Level:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {ip.is_bad === true ? 'High Risk' : ip.is_bad === false ? 'Low Risk' : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlutoIPDet